import { useEffect, useState } from "react";
import { QuizTransport } from "./QuizTransport";
import type { QuizClient, QuizJoinOpts, QuizRoomState } from "./types";

/**
 * Connect to a Quizzle room for the lifetime of the calling page. Unlike the
 * runner's useMpRoom (which survives route changes lobby→play→results), the
 * Quizzle host + controller each stay on a single page through every phase,
 * so a straightforward connect-on-mount hook is enough.
 *
 * `opts` MUST be memoised by the caller (stable code/role/id) or the socket
 * will reconnect every render.
 */
export function useQuizRoom(opts: QuizJoinOpts | null): {
  client: QuizClient | null;
  state: QuizRoomState | null;
  error: string | null;
} {
  const [client, setClient] = useState<QuizClient | null>(null);
  const [state, setState] = useState<QuizRoomState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opts) return;
    const c = new QuizTransport();
    setClient(c);
    const off = c.on((e) => {
      if (e.type === "state") {
        // A valid snapshot means we're connected — clear any earlier transient
        // error (e.g. a join that raced the host's room creation, since the
        // transport auto-reconnects and rejoins).
        setError(null);
        setState((prev) => {
          const nr = e.state.rev;
          const cr = prev?.rev;
          // Drop stale OR duplicate (heartbeat) snapshots — only re-render when
          // the rev actually advances, so the 1.5s resync adds no churn.
          if (typeof nr === "number" && typeof cr === "number" && nr <= cr) return prev;
          return e.state;
        });
      } else if (e.type === "error") {
        setError(e.message);
      }
    });
    c.join(opts).then(setState).catch((err) => setError((err as Error).message ?? "Could not join Quizzle"));
    return () => {
      off();
      c.leave();
      setClient(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts?.code, opts?.role, opts?.me.id]);

  return { client, state, error };
}
